import { Component, OnInit, Input, SimpleChanges, OnChanges, OnDestroy, Output, EventEmitter } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { CustomerService } from '../../../api/customer.service';
import { UserProfileService } from '../../../api/user-profile.service';
import { FormService } from '../../../api/form.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import uuid from '../../../utils/uuid';
import { DataObject } from '../../../interfaces/data-objects';
import { TransactionTypes } from '../../../models/transactions-enum';
import { Countries } from '../../../utils/countries-enum';

@UntilDestroy()
@Component({
  selector: 'iv-parties',
  templateUrl: './parties.component.html',
  styleUrls: ['./parties.component.scss']
})
export class PartiesComponent implements OnInit, OnChanges, OnDestroy {

  @Input() transaction: any;
  @Input() buyerDetails = [];
  @Input() userCountry: Countries;
  @Input() disabled = false;
  @Output() saveTransaction: EventEmitter<DataObject>;

  uuid = uuid();
  represented_party: DataObject[] = [];
  counter_party: DataObject[] = [];
  edpAccountContactInfo = {};
  companyFullName: string = '';
  flexPercent = '50';
  notesForm: UntypedFormGroup;
  enableInCapacityAndCareOfNotes = false;

  constructor(
    private customerService: CustomerService, 
    private userProfileService: UserProfileService,
    private formService: FormService,
    private fb: UntypedFormBuilder) {
      this.saveTransaction = new EventEmitter<DataObject>();
    }

  ngOnInit() {
    this.setupForm();
    this.getAccountInfo();
    this.enableInCapacityAndCareOfNotes = [TransactionTypes.VALUATION, TransactionTypes.CONSULTATION, TransactionTypes.CONSULTING, TransactionTypes.LEASE, TransactionTypes.SALE].includes(this.transaction.transaction_type)
      && this.userCountry === Countries.Singapore;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.transaction) {
      return;
    }
    if (changes.transaction.currentValue) {
      const { represented_party, counter_party, multiple_buyers, rep_role, bill_to_third_party_customer } = changes.transaction.currentValue;
      if (represented_party && !bill_to_third_party_customer) {
        this.customerService.getById(this.transaction.represented_party)
          .pipe(untilDestroyed(this))
          .subscribe((response: DataObject) => {
            if (response?.results) {
              this.represented_party = response.results;
              if (this.represented_party && this.represented_party.length > 0) {
                this.companyFullName = `${this.represented_party[0].name1}`;
                // Filtering address
                this.represented_party[0].addresses.addresses = response.results[0].addresses.addresses.filter(
                  address => +address.address_seq_num === +this.transaction.represented_address);
              }
            }
          });
      } else {
        this.companyFullName = represented_party;
      }
      if (counter_party) {
        this.customerService.getById(this.transaction.counter_party).subscribe((response) => {
          this.counter_party = response.results;
        });
      }
      if ((rep_role === 'seller' && multiple_buyers) || (rep_role === 'landlord' && this.transaction.multiple_tenants)) {
        this.flexPercent = '100';
      }
    }
  }

  setupForm() {
    this.notesForm = this.fb.group({
      care_of_notes: [
        { value: this.transaction?.care_of_notes || '', disabled: this.disabled },
      ],
      in_capacity_notes: [
        { value: this.transaction?.in_capacity_notes || '', disabled: this.disabled },
      ],
    });
    this.formService.addLoadedForm(this.notesForm, this.uuid);
    this.notesForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe((changes: any) => {
        this.saveTransaction.emit(changes);
      });
  }

  hideOtherPartyAddress() {
    let hideAddress = false;
    switch (this.transaction.rep_role) {
      case 'buyer':
      case 'seller':
      case 'tenant':
      case 'landlord':
      case 'borrower':
      case 'lender':
        hideAddress = true;
        break;
      default:
        break;
    }
    return hideAddress;
  }

  isPacificIsolationEnable(): boolean {
    if (this.transaction.is_residential_business_line || this.transaction.is_ip_metro_business_line) {
      return false;
    }
    return this.userProfileService.isPacificIsolationEnable(this.transaction);
  }

  getAccountInfo() {
    if (this.isPacificIsolationEnable()) {

      if (this.transaction.edp_represented_party) {
        this.customerService.getByEDPCompanyId(this.transaction.edp_represented_party).pipe(untilDestroyed(this))
          .subscribe((customers: any) => {
            this.edpAccountContactInfo['represented_party_account'] = customers?.company_name;
          });
      }

      if (this.transaction.edp_represented_party_contact) {
        this.customerService.getByEDPContactId(this.transaction.edp_represented_party_contact).pipe(untilDestroyed(this))
          .subscribe((contacts: any) => {
            this.edpAccountContactInfo['represented_party_contact'] = contacts?.contact_display_name;
          });
      }

      if (this.transaction.edp_counter_party) {
        this.customerService.getByEDPCompanyId(this.transaction.edp_counter_party).pipe(untilDestroyed(this))
          .subscribe((customers: any) => {
            this.edpAccountContactInfo['counter_party_account'] = customers?.company_name;
          });
      }

      if (this.transaction.edp_counter_party_contact) {
        this.customerService.getByEDPContactId(this.transaction.edp_counter_party_contact).pipe(untilDestroyed(this))
          .subscribe((contacts: any) => {
            this.edpAccountContactInfo['counter_party_contact'] = contacts?.contact_display_name;
          });
      }

      if (this.transaction.deal_type_agile_space === 'fill') {
        if (this.transaction.edp_other_party) {
          this.customerService.getByEDPCompanyId(this.transaction.edp_other_party).pipe(untilDestroyed(this))
            .subscribe((customers: any) => {
              this.edpAccountContactInfo['other_party_account'] = customers?.company_name;
            });
        }

        if (this.transaction.edp_other_party_contact) {
          this.customerService.getByEDPContactId(this.transaction.edp_other_party_contact).pipe(untilDestroyed(this))
            .subscribe((contacts: any) => {
              this.edpAccountContactInfo['other_party_contact'] = contacts?.contact_display_name;
            });
        }
      }
    }
  }

  ngOnDestroy() {}
}
